import { QueryKey, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  UpdateCustomerDocumentMutation,
  UpdateCustomerDocumentMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { customerDocumentsKey } from "./useCustomerDocuments";

const UPDATE_CUSTOMER_DOCUMENT_QUERY = graphql(`
  mutation updateCustomerDocument($request: UpdateCustomerDocumentRequest!) {
    updateCustomerDocument(UpdateCustomerDocumentRequest: $request) {
      id
      resourceId
      fileName
      description
      documentType
      uiPath
      isFolder
    }
  }
`);

export function useUpdateCustomerDocument(
  options?: UseMutationOptions<
    UpdateCustomerDocumentMutation,
    unknown,
    UpdateCustomerDocumentMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(UPDATE_CUSTOMER_DOCUMENT_QUERY, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const updatedDocs = data.updateCustomerDocument;
      const keysToInvalidate = new Map<string, QueryKey>(); // stringified key → actual key. Makes sure duplicates don't happen
      for (const update of variables.request.updates) {
        const prevUiPath = update.previousUiPath ?? null;

        for (const doc of updatedDocs) {
          const { documentType, resourceId, uiPath: currentUiPath } = doc;

          if (!documentType) continue;

          // Previous folder
          const prevKey = customerDocumentsKey({
            documentType,
            resourceId,
            uiPath: prevUiPath ?? null,
          });
          keysToInvalidate.set(JSON.stringify(prevKey), prevKey);

          // New folder (only if different)
          if (prevUiPath !== currentUiPath) {
            const newKey = customerDocumentsKey({
              documentType,
              resourceId,
              uiPath: currentUiPath ?? null,
            });
            keysToInvalidate.set(JSON.stringify(newKey), newKey);
          }
        }
      }

      // Batch invalidate all relevant queries
      for (const queryKey of keysToInvalidate.values()) {
        await queryClient.invalidateQueries({ queryKey });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data: data, ...rest };
}
